import type { QueryClient } from '@tanstack/react-query';
import { useWorkspaceStore } from '@/stores/workspace-store';
import { useConnectionStore } from '@/stores/connection-store';
import { useTunnelStore } from '@/stores/tunnel-store';
import type { CommandContext } from './types';

/**
 * Snapshot of every piece of app state a command might care about. Built
 * fresh each time the command bar opens so `when()` guards and `run()`
 * handlers see the current world without stale closures or React effects.
 *
 * Keep this pure — no subscriptions, no network calls. Everything flows
 * from Zustand `.getState()`.
 */
export function buildCommandContext(queryClient: QueryClient | null = null): CommandContext {
  const ws = useWorkspaceStore.getState();
  const conn = useConnectionStore.getState();
  const tun = useTunnelStore.getState();

  const activeTab = ws.tabs.find((t) => t.id === ws.activeTabId) ?? null;

  const activeConnectionIds = Object.entries(conn.activeConnections)
    .filter(([, v]) => v.status === 'connected')
    .map(([k]) => k);

  const activeTunnelIds = Object.entries(tun.activeTunnels)
    .filter(([, v]) => v.status === 'running')
    .map(([k]) => k);

  const activeConnectionId =
    (activeTab && 'connectionId' in activeTab
      ? (activeTab as { connectionId?: string }).connectionId ?? null
      : null);

  return {
    activeTab,
    openTabs: ws.tabs,
    activeConnectionId,
    activeConnections: activeConnectionIds,
    activeTunnels: activeTunnelIds,
    // TODO: wire real user role once the auth context exposes it here
    role: 'admin',
    queryClient,
    navigate: (to) => {
      if (to.type === 'tab') ws.switchTab(to.tabId);
    },
  };
}
